import { useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useAuthStore } from '../../store/authStore'
import AdminDashboard from './roles/AdminDashboard'
import EventOrganizerDashboard from './roles/EventOrganizerDashboard'
import CoachDashboard from './roles/CoachDashboard'
import AthleteDashboard from './roles/AthleteDashboard'
import ParentDashboard from './roles/ParentDashboard'
import ScoutDashboard from './roles/ScoutDashboard'
import StationOperatorView from './StationOperatorView'

/**
 * Role-based dashboard component that renders different dashboard views
 * based on the authenticated user's role and scopes
 */
export default function RoleBasedDashboard() {
  const { user } = useAuth()
  // Select the boolean results (not the hasScope function, whose stable
  // reference would prevent re-render when scopes load asynchronously).
  const hasStationOperatorScope = useAuthStore(state => state.hasScope('STATION_OPERATOR'))
  const hasSchoolWideScope = useAuthStore(state => state.hasScope('SCHOOL_WIDE'))
  const [stationMode, setStationMode] = useState(false)

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  // Station mode takes over the entire view
  if (stationMode) {
    return <StationOperatorView onExitStationMode={() => setStationMode(false)} />
  }

  const isElevatedCoach = user.role === 'COACH' && hasSchoolWideScope

  // Station Mode toggle banner
  const stationModeToggle = hasStationOperatorScope ? (
    <div className="mb-4">
      <button
        onClick={() => setStationMode(true)}
        className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
      >
        Enter Station Mode
      </button>
    </div>
  ) : null

  // Render role-specific dashboard
  const renderDashboard = () => {
    switch (user.role) {
      case 'ADMIN':
        return <AdminDashboard />

      case 'EVENT_ORGANIZER':
        return <EventOrganizerDashboard />

      case 'COACH':
        return <CoachDashboard elevated={isElevatedCoach} />

      case 'ATHLETE':
        return <AthleteDashboard />

      case 'PARENT':
        return <ParentDashboard />

      case 'SCOUT':
        return <ScoutDashboard />

      default:
        return (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Unknown User Role</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Your account role ({user.role}) is not recognized. Please contact support for
                    assistance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )
    }
  }

  return (
    <>
      {stationModeToggle}
      {renderDashboard()}
    </>
  )
}
